import React, { useState, useEffect } from 'react';
import { dataMeet } from '../../data/data_meet';
import './SharingPanel.css';

function SharingPanel({ isOpen, onClose, instanceId, meetingName, meetingData }) {
  const [sharing, setSharing] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [addingUser, setAddingUser] = useState(false);
  const [removingUser, setRemovingUser] = useState(null);
  const [isClosing, setIsClosing] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [currentPermissions, setCurrentPermissions] = useState('');
  const [newPermissions, setNewPermissions] = useState('');
  const [updatingPermissions, setUpdatingPermissions] = useState(false);
  const [showPermissionsTooltip, setShowPermissionsTooltip] = useState(false);
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [showEmailError, setShowEmailError] = useState(false);

  useEffect(() => {
    if (isOpen && instanceId) {
      fetchSharing();
      fetchInstanceDetails();
    }
  }, [isOpen, instanceId]);

  const fetchSharing = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await dataMeet.getInstanceSharing(instanceId);
      if (response.success) {
        setSharing(response.sharing || []);
      } else {
        setError('Failed to load sharing information');
      }
    } catch (err) {
      setError('Error loading sharing: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchInstanceDetails = async () => {
    try {
      const response = await dataMeet.getInstance(instanceId);
      if (response.success) {
        const permissions = response.instance?.permissions || 'private';
        setCurrentPermissions(permissions);
        setNewPermissions(permissions);
      }
    } catch (err) {
      console.error('Error loading instance details:', err);
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e) => {
    const email = e.target.value;
    setNewUserEmail(email);
    setShowEmailError(false); // Hide error when user starts typing
  };

  const handleAddSharing = async (e) => {
    e.preventDefault();
    if (!newUserEmail.trim()) {
      setEmailError('Please enter an email address');
      setShowEmailError(true);
      return;
    }
    
    if (!validateEmail(newUserEmail.trim())) {
      setEmailError('Please enter a valid email address');
      setShowEmailError(true);
      return;
    }

    try {
      setAddingUser(true);
      setError(null);
      setEmailError('');
      setShowEmailError(false);
      const response = await dataMeet.addInstanceSharing(instanceId, newUserEmail.trim());
      if (response.success) {
        setNewUserEmail('');
        setShowAddUserForm(false); // Hide the form after successful addition
        await fetchSharing(); // Refresh the list
      } else {
        setError('Failed to add sharing');
      }
    } catch (err) {
      setError('Error adding sharing: ' + err.message);
    } finally {
      setAddingUser(false);
    }
  };

  const handleRemoveSharing = async (userId) => {
    try {
      setRemovingUser(userId);
      setError(null);
      const response = await dataMeet.removeInstanceSharing(instanceId, userId);
      if (response.success) {
        await fetchSharing(); // Refresh the list
      } else {
        setError('Failed to remove sharing');
      }
    } catch (err) {
      setError('Error removing sharing: ' + err.message);
    } finally {
      setRemovingUser(null);
    }
  };

  const handlePermissionsChange = async (newPermissionValue) => {
    if (newPermissionValue === currentPermissions) return;

    // Check if trying to change from shared to private with existing sharing records
    if (currentPermissions === 'shared' && newPermissionValue === 'private' && sharing.length > 0) {
      setError('Cannot change permissions from "shared" to "private" while sharing records exist. Please remove all sharing records first.');
      setNewPermissions(currentPermissions); // Reset to current value
      return;
    }

    try {
      setUpdatingPermissions(true);
      setError(null);
      const response = await dataMeet.changeInstancePermissions(instanceId, newPermissionValue);
      if (response.success) {
        setCurrentPermissions(newPermissionValue);
        setNewPermissions(newPermissionValue);
        // If changing to private, clear sharing list
        if (newPermissionValue === 'private') {
          setSharing([]);
        }
      } else {
        setError(response.error || 'Failed to update permissions');
        setNewPermissions(currentPermissions); // Reset to current value
      }
    } catch (err) {
      setError('Error updating permissions: ' + err.message);
      setNewPermissions(currentPermissions); // Reset to current value
    } finally {
      setUpdatingPermissions(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getShortId = (meeting) => {
    if (!meeting) return 'N/A';
    
    return meeting.short_id || 
           meeting.instance?.short_id || 
           meeting.data?.short_id ||
           meeting.data?.instance?.short_id ||
           meeting.instance_id?.substring(0, 8) || 
           meeting.meetingId?.substring(0, 8) || 
           meeting._id?.substring(0, 8) || 
           meeting.id?.substring(0, 8) || 
           'N/A';
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="sharing-panel-backdrop" onClick={handleClose}></div>
      
      {/* Panel */}
      <div className={`sharing-panel ${isClosing ? 'closing' : ''}`}>
        <div className="sharing-panel-header">
          <h2>Meeting Sharing Details</h2>
          <button className="sharing-panel-close" onClick={handleClose}>
            <i className="ti ti-x"></i>
          </button>
        </div>

        <div className="sharing-panel-content">
          <div className="meeting-info">
            <h3>{meetingName || 'Untitled Meeting'}</h3>
            <p className="meeting-id">Meeting ID: {getShortId(meetingData)}</p>
          </div>

          <div className="sharing-description">
            <h5>How Sharing Works</h5>
            <p>When you share a meeting with someone, they will be able to view the meeting details, transcript, and insights. Shared users can access the meeting using the meeting ID or through their own meeting list.</p>
          </div>

          {/* Permissions Section */}
          <div className="permissions-section">
            <h4>
              Meeting Permissions
              <div className="permissions-help">
                <button
                  className="permissions-help-icon"
                  onMouseEnter={() => setShowPermissionsTooltip(true)}
                  onMouseLeave={() => setShowPermissionsTooltip(false)}
                  title="Permissions help"
                >
                  <i className="ti ti-help"></i>
                </button>
                <div className={`permissions-tooltip ${showPermissionsTooltip ? 'show' : ''}`}>
                  <p><strong>Private:</strong> Only you can access this meeting</p>
                  <p><strong>Shared:</strong> You can share with specific users</p>
                  <p><strong>Public:</strong> Anyone with the meeting ID can access</p>
                </div>
              </div>
            </h4>
            <div className="permissions-controls">
              <select
                value={newPermissions}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setNewPermissions(newValue);
                  handlePermissionsChange(newValue);
                }}
                className="permissions-select"
                disabled={updatingPermissions}
              >
                <option value="private">Private</option>
                <option value="shared">Shared</option>
                <option value="public">Public</option>
              </select>
              {updatingPermissions && (
                <div className="permissions-loading">
                  <i className="ti ti-loader ti-spin"></i>
                  Updating...
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="sharing-error">
              {error}
            </div>
          )}

          {/* Current sharing list - only show when permissions are "shared" AND there are sharing records */}
          {newPermissions === 'shared' && sharing.length > 0 && (
            <div className="current-sharing-section">
              <h4>Currently Shared With</h4>
              {loading ? (
                <div className="sharing-loading">Loading sharing information...</div>
              ) : (
                <div className="sharing-list">
                  {sharing.map((share, index) => (
                    <div key={index} className="sharing-item">
                                          <div className="sharing-user-info">
                      <div className="sharing-user-email">{share.user_id}</div>
                      <div className="sharing-details">
                        <span className="sharing-created-by">Added by: {share.created_by}</span>
                        <span> - </span>
                        <span className="sharing-date">{formatDate(share.created_at)}</span>
                      </div>
                    </div>
                      <button
                        className="remove-sharing-btn"
                        onClick={() => handleRemoveSharing(share.user_id)}
                        disabled={removingUser === share.user_id}
                        title="Remove sharing"
                      >
                        {removingUser === share.user_id ? (
                          <i className="ti ti-loader ti-spin"></i>
                        ) : (
                          <i className="ti ti-trash"></i>
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Add new user section - only show when permissions are "shared" */}
          {newPermissions === 'shared' && (
            <div className="add-sharing-section">
              {!showAddUserForm ? (
                <div className="add-user-button-container">
                  <button
                    onClick={() => setShowAddUserForm(true)}
                    className="add-user-toggle-btn"
                  >
                    <i className="ti ti-plus"></i>
                    Add User
                  </button>
                </div>
              ) : (
                <div className="add-user-form-container">
                  <h4>Add User</h4>
                  <form onSubmit={handleAddSharing} className="add-sharing-form">
                    <div>
                      <div className="email-input-container">
                        <input
                          type="email"
                          placeholder="Enter user email"
                          value={newUserEmail}
                          onChange={handleEmailChange}
                          className={`sharing-email-input ${showEmailError && emailError ? 'error' : ''} ${newUserEmail && validateEmail(newUserEmail) ? 'valid' : ''}`}
                          disabled={addingUser}
                          required
                          autoFocus
                        />
                        {newUserEmail && validateEmail(newUserEmail) && (
                          <div className="email-valid-icon">
                            <i className="ti ti-check"></i>
                          </div>
                        )}
                      </div>
                      {showEmailError && emailError && (
                        <div className="email-error-message">{emailError}</div>
                      )}
                    </div>
                    <button 
                      type="submit" 
                      className="add-sharing-btn"
                      disabled={addingUser || !newUserEmail.trim() || emailError}
                    >
                      {addingUser ? 'Adding...' : 'Add'}
                    </button>
                  </form>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default SharingPanel; 